import { wordApi } from '@/api/word.api';
import { isOfflineDbAvailable, isReserveFresh, phraseReserve } from '@/features/offline';

/**
 * Phrases of "Enregistrer" kept ahead for offline work. The session reads
 * batches of 5 by page number; the reserve stores the same list in chunks of
 * 100 (the most the endpoint returns), so batch `page` is a slice of one chunk.
 */

export const SPEAK_RESERVE_CHUNK_SIZE = 100;
/** Chunks kept from the current one on: about 300 phrases ahead. */
export const SPEAK_RESERVE_CHUNKS_AHEAD = 3;

/** The chunk (1-based) holding the first phrase of batch `page`, and its offset in it. */
export function speakReserveSlot(page: number, pageSize: number): { chunk: number; offset: number } {
  const start = (Math.max(1, page) - 1) * pageSize;
  return { chunk: Math.floor(start / SPEAK_RESERVE_CHUNK_SIZE) + 1, offset: start % SPEAK_RESERVE_CHUNK_SIZE };
}

/** Batch `page` from the reserve, shaped like the API answer, or null when it is not there. */
export async function readSpeakBatchFromReserve(
  ownerId: string,
  lang: string,
  page: number,
  pageSize: number,
): Promise<{ data: unknown[] } | null> {
  if (!isOfflineDbAvailable()) return null;
  const { chunk, offset } = speakReserveSlot(page, pageSize);
  const record = await phraseReserve.get<unknown>(ownerId, 'speak', lang, chunk);
  if (!record) return null;
  let items = record.items.slice(offset, offset + pageSize);
  // A batch size that does not divide 100 can straddle two chunks.
  if (items.length < pageSize && record.items.length === SPEAK_RESERVE_CHUNK_SIZE) {
    const next = await phraseReserve.get<unknown>(ownerId, 'speak', lang, chunk + 1);
    if (next) items = items.concat(next.items.slice(0, pageSize - items.length));
  }
  return items.length > 0 ? { data: items } : null;
}

const toppingUp = new Set<string>();

/**
 * Makes sure the chunks from batch `page` on are stored and recent, fetching
 * the missing ones one at a time, and drops those well behind the session.
 * Runs in the background while online; any failure is left for the next call.
 */
export async function topUpSpeakReserve(
  ownerId: string,
  lang: string,
  page: number,
  pageSize: number,
  fetchChunk: (chunk: number) => Promise<{ data?: unknown[] }> = (chunk) =>
    wordApi.getFilesByLang(lang, chunk, SPEAK_RESERVE_CHUNK_SIZE),
): Promise<void> {
  if (!isOfflineDbAvailable() || !ownerId || !lang) return;
  const key = `${ownerId}|${lang}`;
  if (toppingUp.has(key)) return;
  toppingUp.add(key);
  try {
    const { chunk: first } = speakReserveSlot(page, pageSize);
    for (let chunk = first; chunk < first + SPEAK_RESERVE_CHUNKS_AHEAD; chunk += 1) {
      const existing = await phraseReserve.get(ownerId, 'speak', lang, chunk);
      if (isReserveFresh(existing)) {
        if (existing.items.length < SPEAK_RESERVE_CHUNK_SIZE) break;
        continue;
      }
      const data = (await fetchChunk(chunk)).data ?? [];
      if (!data.length) break;
      await phraseReserve.save(ownerId, 'speak', lang, chunk, data);
      // A short chunk is the end of the list.
      if (data.length < SPEAK_RESERVE_CHUNK_SIZE) break;
    }
    // One chunk behind is kept for "previous"; older ones are done with.
    for (const record of await phraseReserve.chunks(ownerId, 'speak', lang)) {
      if (record.chunk < first - 1) await phraseReserve.remove(ownerId, 'speak', lang, record.chunk);
    }
  } finally {
    toppingUp.delete(key);
  }
}
